import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { colors } from '@startengine/design-system.theme.main';

export const Card = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: '20px',
  marginTop: '12px',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${colors.gray5}`,
  borderRadius: '16px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.06), 0px 1px 4px rgba(0, 0, 0, 0.04)',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    width: '369px',
    background: 'linear-gradient(64.45deg, #FFFFFF 16.05%, #efeff0 69.18%)',
  },
}));

export const HeadingTitle = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: 700,
  color: colors.SEBlack,
}));

export const Description = styled(Typography)(() => ({
  fontSize: '14px',
  color: colors.gray1,
  lineHeight: 1.4,
}));

export const RequestButton = styled('button')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: 'auto',
  padding: '13px 16px',
  marginTop: '4px',
  border: `1px solid ${colors.SEDark}`,
  borderRadius: '16px',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: colors.SEDark,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  textTransform: 'none',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.8,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'default',
  },
}));

export const RequestError = styled(Typography)(() => ({
  fontSize: '13px',
  color: colors.Red,
  lineHeight: 1.4,
}));

export const RequestedConfirmation = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '4px',
  padding: '13px 16px',
}));

export const ConfirmationIcon = styled(CheckIcon)(() => ({
  color: colors.SEDark,
  fontSize: 18,
}));

export const ConfirmationText = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 600,
  color: colors.SEDark,
}));

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    margin: '16px',
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiDialog-container': {
      alignItems: 'flex-end',
    },
    '& .MuiDialog-paper': {
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      borderRadius: '16px 16px 0 0',
    },
  },
}));

export const Content = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '28px 24px 24px',
  fontFamily: "'IBM Plex Sans', sans-serif",
}));

export const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  color: colors.gray2,
}));

export const HeaderBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  paddingRight: '24px',
}));

export const HeaderTitle = styled(Typography)(() => ({
  fontSize: '24px',
  fontWeight: 700,
  color: colors.SEBlack,
}));

export const HeaderBody = styled(Typography)(() => ({
  fontSize: '15px',
  color: colors.gray1,
  lineHeight: 1.5,
}));

export const SectionBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const SectionHeadingRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

export const SectionHeading = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: 700,
  color: colors.SEBlack,
}));

export const SectionSubtitle = styled(Typography)(() => ({
  fontSize: '14px',
  color: colors.gray2,
}));

export const OptionalTag = styled(Typography)(() => ({
  display: 'inline-block',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: colors.gray1,
  textTransform: 'uppercase',
  backgroundColor: colors.gray5,
  borderRadius: '4px',
  padding: '3px 8px',
  lineHeight: 1,
}));

export const QuestionField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontSize: '15px',
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
}));

export const SubmitButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'submitted',
})(({ submitted }) => ({
  width: '100%',
  padding: '14px 16px',
  border: 'none',
  borderRadius: '16px',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: '#FFFFFF',
  backgroundColor: submitted ? 'rgba(24, 92, 101, 0.4)' : colors.SEDark,
  cursor: submitted ? 'default' : 'pointer',
  transition: 'background-color 0.2s, opacity 0.2s',
  '&:hover': {
    opacity: submitted ? 1 : 0.9,
  },
}));

export const QrCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: colors.gray6,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const AppLinkButton = styled('a')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  boxSizing: 'border-box',
  padding: '14px 16px',
  border: 'none',
  borderRadius: '16px',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: '#FFFFFF',
  backgroundColor: colors.SEDark,
  textDecoration: 'none',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.9,
  },
}));

export const QrTextBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const QrHeading = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 700,
  color: colors.SEBlack,
}));

export const QrBody = styled(Typography)(() => ({
  fontSize: '13px',
  color: colors.gray1,
  lineHeight: 1.4,
}));
